import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Full-screen premium lightbox. Opens on click, arrow/ESC navigation,
 * kitxens watermark preserved on the enlarged view.
 * Props: open, items [{img|src, label, alt}], index, onClose, onIndex(fn)
 */
export const ImageLightbox = ({ open, items = [], index = 0, onClose, onIndex }) => {
  const count = items.length;
  const prev = useCallback(() => onIndex((index - 1 + count) % count), [index, count, onIndex]);
  const next = useCallback(() => onIndex((index + 1) % count), [index, count, onIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, prev, next, onClose]);

  const item = items[index];

  return (
    <AnimatePresence>
      {open && item && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-neutral-950/90 backdrop-blur-xl p-4 sm:p-8"
          onClick={onClose}
          data-testid="image-lightbox"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-[#F77F11] text-white flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>

          {count > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-3 sm:left-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-[#F77F11] text-white flex items-center justify-center transition-colors"
                aria-label="Previous"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-3 sm:right-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-[#F77F11] text-white flex items-center justify-center transition-colors"
                aria-label="Next"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          <motion.figure
            key={index}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="relative max-w-[92vw] max-h-[86vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-[0_50px_140px_-30px_rgba(0,0,0,0.9)]">
              <img
                src={item.img || item.src}
                alt={item.alt || item.label || ''}
                className="max-w-[92vw] max-h-[76vh] w-auto h-auto object-contain select-none"
                draggable={false}
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute bottom-3 right-3 flex items-center rounded-full bg-black/45 backdrop-blur-md px-2.5 py-1 ring-1 ring-white/10"
              >
                <span className="text-[10px] font-semibold tracking-[-0.005em] text-white/85">
                  kit<span className="text-[#F77F11]">x</span>ens
                </span>
              </div>
            </div>
            {(item.label || count > 1) && (
              <figcaption className="mt-4 flex items-center gap-3 text-white/80">
                {item.label && (
                  <span className="text-[11px] tracking-[0.22em] font-bold uppercase">{item.label}</span>
                )}
                {count > 1 && (
                  <span className="text-[11px] text-white/40 font-semibold">{index + 1} / {count}</span>
                )}
              </figcaption>
            )}
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImageLightbox;
